import React from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../../config/constants';
import '../../App.scss';

const G20GradientVideo = '/assets/video3.mp4';

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [validating, setValidating] = React.useState(true);
    const [tokenValid, setTokenValid] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [success, setSuccess] = React.useState<string | null>(null);

    const hashRoute = React.useMemo(() => {
        const hash = window.location.hash || '';
        if (!hash.startsWith('#/reset-password')) {
            return { uid: '', token: '' };
        }

        const queryIndex = hash.indexOf('?');
        if (queryIndex === -1) {
            return { uid: '', token: '' };
        }

        const params = new URLSearchParams(hash.slice(queryIndex + 1));
        return {
            uid: params.get('uid') ?? '',
            token: params.get('token') ?? '',
        };
    }, []);

    const uid = searchParams.get('uid') ?? hashRoute.uid;
    const token = searchParams.get('token') ?? hashRoute.token;
    const theme = (localStorage.getItem('theme') as 'light' | 'dark') || 'light';

    React.useEffect(() => {
        document.body.classList.remove('theme-light', 'theme-dark');
        document.body.classList.add(`theme-${theme}`);
    }, [theme]);

    React.useEffect(() => {
        let cancelled = false;

        const validateToken = async () => {
            if (!uid || !token) {
                console.log('[ResetPasswordPage] Missing uid or token');
                if (!cancelled) {
                    setError('The reset link is incomplete or invalid.');
                    setTokenValid(false);
                    setValidating(false);
                }
                return;
            }

            console.log('[ResetPasswordPage] Validating token', { uid, token: token.slice(0, 20) + '...' });
            try {
                const query = new URLSearchParams({ uid, token });
                const response = await fetch(`${API_BASE}/api/users/auth/password/validate?${query.toString()}`);
                const payload = await response.json().catch(() => ({}));
                console.log('[ResetPasswordPage] Token validation response:', { status: response.ok, success: payload?.success });

                if (!response.ok || payload?.success === false) {
                    throw new Error(payload?.message || 'This reset link is invalid or expired.');
                }

                if (!cancelled) {
                    setTokenValid(true);
                    setError(null);
                    console.log('[ResetPasswordPage] Token validated successfully');
                }
            } catch (err: any) {
                if (!cancelled) {
                    setTokenValid(false);
                    console.error('[ResetPasswordPage] Token validation failed:', err?.message);
                    setError(err?.message || 'This reset link is invalid or expired.');
                }
            } finally {
                if (!cancelled) {
                    setValidating(false);
                }
            }
        };

        void validateToken();

        return () => {
            cancelled = true;
        };
    }, [token, uid]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (password.length < 8) {
            setError('Password must be at least 8 characters long.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        console.log('[ResetPasswordPage] Submitting password reset');
        setLoading(true);

        try {
            console.log('[ResetPasswordPage] Calling /api/users/auth/password/reset');
            const response = await fetch(`${API_BASE}/api/users/auth/password/reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ uid, token, newPassword: password }),
            });
            const payload = await response.json().catch(() => ({}));
            console.log('[ResetPasswordPage] Reset response:', { status: response.ok, success: payload?.success });

            if (!response.ok || payload?.success === false) {
                throw new Error(payload?.message || 'Failed to reset password.');
            }

            setSuccess(payload?.message || 'Password updated successfully.');
            console.log('[ResetPasswordPage] Password reset successful');
            setTokenValid(false);
            setPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            console.error('[ResetPasswordPage] Password reset failed:', err?.message);
            setError(err?.message || 'Failed to reset password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`App login-page-shell theme-${theme}`}>
            <video className='header-bar-bg-video' autoPlay loop muted playsInline src={G20GradientVideo} />
            <div className='login-page-overlay' />

            <Container maxWidth='sm' className='login-page-content'>
                <Box className='login-card reset-password-card'>
                    <Typography component='h1' variant='h4' sx={{ marginBottom: 2, fontWeight: 600 }}>
                        Reset your password
                    </Typography>

                    <Typography className='login-dialog-copy'>
                        Choose a new password for your XM account. This link can only be used once.
                    </Typography>

                    {validating && <Alert severity='info' sx={{ width: '100%', marginTop: 2 }}>Checking reset link...</Alert>}

                    {error && (
                        <Alert severity='error' sx={{ width: '100%', marginTop: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity='success' sx={{ width: '100%', marginTop: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {tokenValid && !validating && (
                        <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%', marginTop: 2 }}>
                            <TextField
                                fullWidth
                                label='New password'
                                type='password'
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                margin='normal'
                                autoComplete='new-password'
                                required
                                disabled={loading}
                            />

                            <TextField
                                fullWidth
                                label='Confirm new password'
                                type='password'
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                margin='normal'
                                autoComplete='new-password'
                                required
                                disabled={loading}
                            />

                            <Button fullWidth variant='contained' type='submit' disabled={loading} sx={{ marginTop: 3 }}>
                                {loading ? 'Updating password...' : 'Update password'}
                            </Button>
                        </Box>
                    )}

                    <Button
                        variant='text'
                        className='login-secondary-link-button'
                        onClick={() => {
                            window.location.hash = '';
                            navigate('/');
                        }}
                        sx={{ marginTop: 2 }}
                    >
                        Back to login
                    </Button>
                </Box>
            </Container>
        </div>
    );
}